/**
 * Channel Configuration — shadcn/Tailwind
 * Email, SMS, WhatsApp notification channel configuration
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../components/ui/use-toast';
import { Save, Mail, MessageSquare, Phone, Send, Settings, Info } from 'lucide-react';
import { notificationService, NotificationChannel } from '../../services/notificationService';
import { PageHeader } from '../ui/page-header';

// Email Configuration Form
const EmailConfigForm: React.FC<{ channelId: string }> = ({ channelId }) => {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [channel, setChannel] = useState<NotificationChannel | null>(null);
  const [form, setForm] = useState({
    mail_driver: 'smtp', host: '', port: 587, encryption: 'tls',
    username: '', password: '', from_address: '', from_name: '',
    is_active: false, test_email: '',
  });

  const loadChannelData = async () => {
    try {
      setLoading(true);
      const channels = await notificationService.getEmailChannels();
      if (channels.data && channels.data.length > 0) {
        const emailChannel = channels.data.sort((a: any, b: any) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        )[0];
        setChannel(emailChannel);
        const c = emailChannel.configuration;
        setForm({
          mail_driver: c.mail_driver || 'smtp', host: c.host || '', port: c.port || 587,
          encryption: c.encryption || 'tls', username: c.username || '', password: c.password || '',
          from_address: c.from_address || '', from_name: c.from_name || '',
          is_active: emailChannel.is_active || false, test_email: '',
        });
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { loadChannelData(); }, [channelId]);

  const updateForm = (key: string, value: any) => setForm((p) => ({ ...p, [key]: value }));

  const handleHostChange = (host: string) => {
    updateForm('host', host);
    const h = host.toLowerCase();
    if (h.includes('gmail') || h.includes('outlook') || h.includes('hotmail') || h.includes('yahoo')) {
      setForm((p) => ({ ...p, host, port: 587, encryption: 'tls' }));
    }
  };

  const handleEncryptionChange = (v: string) => {
    const portMap: Record<string, number> = { ssl: 465, tls: 587, none: 25 };
    setForm((p) => ({ ...p, encryption: v, port: portMap[v] || p.port }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const result = await notificationService.saveEmailChannel({ ...form, encryption: form.encryption as 'none' | 'tls' | 'ssl' }, channel?.id);
      if (result.error) toast({ title: `Failed: ${result.error}`, variant: 'destructive' });
      else { toast({ title: 'Email configuration saved', variant: 'success' }); await loadChannelData(); }
    } catch { toast({ title: 'Failed to save email configuration', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const handleTest = async () => {
    if (!form.test_email) { toast({ title: 'Please enter a test email', variant: 'destructive' }); return; }
    setTesting(true);
    try {
      let testId = channel?.id;
      if (!testId) {
        const temp = await notificationService.saveEmailChannel({ ...form, is_active: false, encryption: form.encryption as 'none' | 'tls' | 'ssl' });
        if (temp.error) { toast({ title: `Failed: ${temp.error}`, variant: 'destructive' }); return; }
        testId = temp.data.id;
      }
      const result = await notificationService.testEmail(testId, form.test_email, form);
      if (result.error) toast({ title: `Test failed: ${result.error}`, variant: 'destructive' });
      else toast({ title: 'Test email sent', variant: 'success' });
    } catch { toast({ title: 'Failed to send test email', variant: 'destructive' }); }
    finally { setTesting(false); }
  };

  return (
    <Card>
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-2"><Mail className="h-4 w-4" /><span className="font-semibold">Email Configuration</span></div>
        <Badge>SMTP</Badge>
      </div>
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2"><Label>Mail Driver *</Label>
            <Select value={form.mail_driver} onValueChange={(v) => updateForm('mail_driver', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="smtp">SMTP</SelectItem><SelectItem value="sendgrid">SendGrid</SelectItem><SelectItem value="mailgun">Mailgun</SelectItem><SelectItem value="ses">Amazon SES</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Host *</Label><Input placeholder="smtp.gmail.com" value={form.host} onChange={(e) => handleHostChange(e.target.value)} /><p className="text-xs text-muted-foreground">Gmail: smtp.gmail.com, Outlook: smtp-mail.outlook.com</p></div>
          <div className="space-y-2"><Label>Port *</Label><Input type="number" min={1} max={65535} value={form.port} onChange={(e) => updateForm('port', parseInt(e.target.value))} /><p className="text-xs text-muted-foreground">TLS: 587, SSL: 465, None: 25</p></div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2"><Label>Username *</Label><Input placeholder="your-email@gmail.com" value={form.username} onChange={(e) => updateForm('username', e.target.value)} /></div>
          <div className="space-y-2"><Label>Password *</Label><Input type="password" placeholder="Password or App Password" value={form.password} onChange={(e) => updateForm('password', e.target.value)} /></div>
          <div className="space-y-2"><Label>Encryption *</Label>
            <Select value={form.encryption} onValueChange={handleEncryptionChange}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="tls">TLS (Port 587 - Recommended)</SelectItem><SelectItem value="ssl">SSL (Port 465)</SelectItem><SelectItem value="none">None (Port 25 - Not Secure)</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label>From Address *</Label><Input type="email" placeholder="noreply@labboxx.com" value={form.from_address} onChange={(e) => updateForm('from_address', e.target.value)} /></div>
          <div className="space-y-2"><Label>From Name *</Label><Input placeholder="LabBoxx LIMS" value={form.from_name} onChange={(e) => updateForm('from_name', e.target.value)} /></div>
        </div>
        <Separator />
        <div className="grid grid-cols-3 gap-4 items-end">
          <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => updateForm('is_active', v)} /><Label>Active</Label></div>
          <div className="space-y-2"><Label>Test Email</Label><Input placeholder="test@example.com" value={form.test_email} onChange={(e) => updateForm('test_email', e.target.value)} /></div>
          <Button variant="outline" onClick={handleTest} disabled={testing}><Send className="h-4 w-4 mr-1" />{testing ? 'Sending...' : 'Send Test Email'}</Button>
        </div>
        <Separator />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={loadChannelData}>Reset</Button>
          <Button onClick={handleSave} disabled={loading}><Save className="h-4 w-4 mr-1" />Save Configuration</Button>
        </div>
      </div>
    </Card>
  );
};

// The device dialects `organization/sms_service.py` can actually speak. A
// dropdown entry with no send path behind it is worse than a missing one: it
// looks configured and silently delivers nothing.
const GATEWAY_MODELS = [
  { value: 'UC2000 Series (Dinstar)', label: 'UC2000 Series (Dinstar)' },
  { value: 'UC200 (Dinstar)', label: 'UC200 (Dinstar)' },
  { value: 'UC120 (Dinstar)', label: 'UC120 (Dinstar)' },
  { value: 'UC100 (Dinstar)', label: 'UC100 (Dinstar)' },
  { value: 'DWG2000C (Dinstar)', label: 'DWG2000C (Dinstar)' },
  { value: 'GoIP GSM Gateway', label: 'GoIP GSM Gateway' },
  { value: 'Kannel SMS Gateway', label: 'Kannel SMS Gateway' },
  { value: 'Generic JSON POST', label: 'Generic JSON POST' },
];

// SMS Configuration Form
const SMSConfigForm: React.FC<{ channelId: string }> = ({ channelId }) => {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [channel, setChannel] = useState<NotificationChannel | null>(null);
  // A load that failed must not look like a blank configuration — see
  // `loadChannelData`.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState({
    provider: 'generic_gateway', account_sid: '', auth_token: '', from_number: '', webhook_url: '', is_active: false,
    sms_network_mode: 'auto', sms_lan_gateway_url: '', sms_wan_gateway_url: '',
    sms_auto_discovery_enabled: true, sms_connection_timeout: 10, sms_endpoint_test_interval: 300,
    // On-premises GSM gateway. These map onto the `sms_*` columns that
    // `EnterpriseSMSService._send_generic_gateway_sms` reads — the Dinstar
    // send path has existed in the backend all along, with no screen to reach
    // it, so this form could only ever configure a cloud provider.
    gateway_url: '', gateway_port: '', gateway_model: 'UC2000 Series (Dinstar)',
    sim_slot: '0', http_method: 'POST', content_type: 'application/json',
    custom_parameters: '',
    test_phone: '',
  });

  const isGateway = form.provider === 'generic_gateway';

  const loadChannelData = async () => {
    try {
      setLoading(true);
      const channels = await notificationService.getSMSChannels();
      if (channels.data && channels.data.length > 0) {
        const smsChannel: any = channels.data[0];
        setChannel(smsChannel);
        // Two shapes arrive here. A tenant returns a NotificationChannel whose
        // settings live under `configuration`; an enterprise org returns its
        // settings row flat, with `sms_`-prefixed columns and no
        // `configuration` at all. Reading `.configuration` unconditionally
        // threw on the enterprise path, and the bare `catch` below swallowed
        // it — which is how this screen came to show Twilio defaults to an org
        // that had a gateway configured.
        const c = smsChannel.configuration || {};
        const flat = smsChannel.configuration ? {} : smsChannel;
        const pick = (cfgKey: string, flatKey: string) =>
          c[cfgKey] ?? (flat as any)[flatKey];

        setForm({
          provider: pick('provider', 'sms_provider') || 'generic_gateway',
          account_sid: pick('account_sid', 'sms_api_key') || '',
          auth_token: pick('auth_token', 'sms_api_secret') || '',
          from_number: pick('from_number', 'sms_from_number') || '',
          webhook_url: c.webhook_url || '',
          is_active: (smsChannel.is_active ?? smsChannel.sms_notifications_enabled) || false,
          sms_network_mode: pick('sms_network_mode', 'sms_network_mode') || 'auto',
          sms_lan_gateway_url: pick('sms_lan_gateway_url', 'sms_lan_gateway_url') || '',
          sms_wan_gateway_url: pick('sms_wan_gateway_url', 'sms_wan_gateway_url') || '',
          sms_auto_discovery_enabled: pick('sms_auto_discovery_enabled', 'sms_auto_discovery_enabled') ?? true,
          sms_connection_timeout: pick('sms_connection_timeout', 'sms_connection_timeout') || 10,
          sms_endpoint_test_interval: pick('sms_endpoint_test_interval', 'sms_endpoint_test_interval') || 300,
          gateway_url: pick('gateway_url', 'sms_gateway_url') || '',
          gateway_port: String(pick('gateway_port', 'sms_gateway_port') || ''),
          gateway_model: pick('gateway_model', 'sms_gateway_model') || 'UC2000 Series (Dinstar)',
          sim_slot: String(pick('sim_slot', 'sms_sim_slot') ?? '0'),
          http_method: pick('http_method', 'sms_http_method') || 'POST',
          content_type: pick('content_type', 'sms_content_type') || 'application/json',
          custom_parameters: pick('custom_parameters', 'sms_custom_parameters') || '',
          test_phone: '',
        });
      }
      setLoadError(null);
    } catch (e: any) {
      setLoadError(
        'The saved SMS configuration could not be read, so the fields below are '
        + 'blank defaults rather than your settings. Nothing has been changed.'
      );
    } finally { setLoading(false); }
  };

  useEffect(() => { loadChannelData(); }, [channelId]);

  const updateForm = (key: string, value: any) => setForm((p) => ({ ...p, [key]: value }));

  const handleSave = async () => {
    setLoading(true);
    try {
      const result = await notificationService.saveSMSChannel(form, channel?.id);
      if (result.error) toast({ title: `Failed: ${result.error}`, variant: 'destructive' });
      else { toast({ title: 'SMS configuration saved', variant: 'success' }); await loadChannelData(); }
    } catch { toast({ title: 'Failed to save SMS configuration', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const handleTest = async () => {
    if (!form.test_phone) { toast({ title: 'Please enter a test phone number', variant: 'destructive' }); return; }
    setTesting(true);
    try {
      let testId = channel?.id;
      if (!testId) {
        const temp = await notificationService.saveSMSChannel({ ...form, is_active: false });
        if (temp.error) { toast({ title: `Failed: ${temp.error}`, variant: 'destructive' }); return; }
        testId = temp.data.id;
      }
      const result = await notificationService.testSMS(testId, form.test_phone, form);
      if (result.error) toast({ title: `Test failed: ${result.error}`, variant: 'destructive' });
      else toast({ title: 'Test SMS sent', variant: 'success' });
    } catch { toast({ title: 'Failed to send test SMS', variant: 'destructive' }); }
    finally { setTesting(false); }
  };

  return (
    <Card>
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-2"><MessageSquare className="h-4 w-4" /><span className="font-semibold">SMS Configuration</span></div>
        <Badge>{isGateway ? 'On-premises gateway' : 'Cloud provider'}</Badge>
      </div>
      <div className="p-6 space-y-4">
        {loadError && (
          <div role="alert" className="rounded-md border border-warning bg-warning-100 p-3 text-sm text-warning-700">
            {loadError}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label>SMS Provider *</Label>
            <Select value={form.provider} onValueChange={(v) => updateForm('provider', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="generic_gateway">On-premises gateway (Dinstar / GoIP / Kannel)</SelectItem>
                <SelectItem value="twilio">Twilio</SelectItem>
                <SelectItem value="aws_sns">AWS SNS</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>From Number *</Label><Input placeholder="+23057654321" value={form.from_number} onChange={(e) => updateForm('from_number', e.target.value)} /></div>
        </div>

        {isGateway ? (
          <>
            <div className="bg-info-100 border border-info-200 rounded-md p-3 flex items-start gap-2">
              <Info className="h-4 w-4 text-info mt-0.5 shrink-0" />
              <p className="text-sm text-foreground">
                A box on your own network with SIM cards in it. Messages leave on the SIM,
                so they cost what the SIM costs and never reach a cloud provider. Dinstar
                units authenticate with HTTP Digest — the username and password below are
                the gateway's own login, not an API key.
              </p>
            </div>
            <Separator />
            <h4 className="font-semibold">Gateway Configuration</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Username</Label><Input placeholder="SuperAdmin" value={form.account_sid} onChange={(e) => updateForm('account_sid', e.target.value)} /></div>
              <div className="space-y-2"><Label>Password</Label><Input type="password" value={form.auth_token} onChange={(e) => updateForm('auth_token', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2"><Label>Gateway URL/IP *</Label><Input placeholder="http://example.dyndns.org:5050/api/send_sms" value={form.gateway_url} onChange={(e) => updateForm('gateway_url', e.target.value)} /></div>
              <div className="space-y-2"><Label>Gateway Port</Label><Input placeholder="80" value={form.gateway_port} onChange={(e) => updateForm('gateway_port', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Gateway Model</Label>
                <Select value={form.gateway_model} onValueChange={(v) => updateForm('gateway_model', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {GATEWAY_MODELS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>SIM Slot / Port</Label>
                <Select value={form.sim_slot} onValueChange={(v) => updateForm('sim_slot', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Port 0 (SIM Slot 1)</SelectItem>
                    <SelectItem value="1">Port 1 (SIM Slot 2)</SelectItem>
                    <SelectItem value="2">Port 2 (SIM Slot 3)</SelectItem>
                    <SelectItem value="3">Port 3 (SIM Slot 4)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Custom Parameters (JSON)</Label>
              <Input
                placeholder='{"timeout": 30, "encoding": "utf8"}'
                value={form.custom_parameters}
                onChange={(e) => updateForm('custom_parameters', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Extra gateway fields, in JSON. Left blank for most installations.</p>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Account SID *</Label><Input placeholder="AC..." value={form.account_sid} onChange={(e) => updateForm('account_sid', e.target.value)} /></div>
              <div className="space-y-2"><Label>Auth Token *</Label><Input type="password" value={form.auth_token} onChange={(e) => updateForm('auth_token', e.target.value)} /></div>
            </div>
            <div className="space-y-2"><Label>Webhook URL</Label><Input placeholder="https://your-domain.com/api/v1/notifications/webhooks/twilio/" value={form.webhook_url} onChange={(e) => updateForm('webhook_url', e.target.value)} /></div>
          </>
        )}

        <Separator />
        <h4 className="font-semibold">Network Routing Configuration</h4>
        <div className="bg-info-100 border border-info-200 rounded-md p-3 flex items-center gap-2">
          <Info className="h-4 w-4 text-info" />
          <p className="text-sm text-foreground">Configure LAN/WAN endpoints for automatic failover and optimized SMS delivery.</p>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2"><Label>Network Mode *</Label>
            <Select value={form.sms_network_mode} onValueChange={(v) => updateForm('sms_network_mode', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-detect (Recommended)</SelectItem><SelectItem value="lan_only">LAN Only</SelectItem>
                <SelectItem value="wan_only">WAN Only</SelectItem><SelectItem value="lan_preferred">LAN Preferred</SelectItem><SelectItem value="wan_preferred">WAN Preferred</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Connection Timeout (s)</Label><Input type="number" min={5} max={60} value={form.sms_connection_timeout} onChange={(e) => updateForm('sms_connection_timeout', parseInt(e.target.value))} /></div>
          <div className="space-y-2"><Label>Test Interval (s)</Label><Input type="number" min={60} max={3600} value={form.sms_endpoint_test_interval} onChange={(e) => updateForm('sms_endpoint_test_interval', parseInt(e.target.value))} /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label>LAN Gateway URL</Label><Input placeholder="http://192.168.1.100:8080" value={form.sms_lan_gateway_url} onChange={(e) => updateForm('sms_lan_gateway_url', e.target.value)} /></div>
          <div className="space-y-2"><Label>WAN Gateway URL</Label><Input placeholder="http://external-gateway.com:8080" value={form.sms_wan_gateway_url} onChange={(e) => updateForm('sms_wan_gateway_url', e.target.value)} /></div>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={form.sms_auto_discovery_enabled} onCheckedChange={(v) => updateForm('sms_auto_discovery_enabled', v)} />
          <Label>Auto Discovery</Label>
          <span className="text-xs text-muted-foreground ml-2">Automatically discover SMS endpoints</span>
        </div>

        <Separator />
        <div className="grid grid-cols-3 gap-4 items-end">
          <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => updateForm('is_active', v)} /><Label>Active</Label></div>
          <div className="space-y-2"><Label>Test Phone</Label><Input placeholder="+1234567890" value={form.test_phone} onChange={(e) => updateForm('test_phone', e.target.value)} /></div>
          <Button variant="outline" onClick={handleTest} disabled={testing}><Send className="h-4 w-4 mr-1" />{testing ? 'Sending...' : 'Send Test SMS'}</Button>
        </div>
        <Separator />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={loadChannelData}>Reset</Button>
          <Button onClick={handleSave} disabled={loading}><Save className="h-4 w-4 mr-1" />Save Configuration</Button>
        </div>

        <div className="bg-info-100 border border-info-200 rounded-md p-4 mt-4">
          {isGateway ? (
            <>
              <p className="font-semibold text-sm mb-2">On-premises gateway setup</p>
              <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
                <li>Put the gateway's send-SMS endpoint in Gateway URL/IP (Dinstar: <code>/api/send_sms</code>)</li>
                <li>Enter the gateway's own login as Username and Password — Dinstar uses HTTP Digest</li>
                <li>Pick the model so the right payload dialect is used, and the SIM slot to send on</li>
                <li>Send a test SMS: the gateway reports its verdict in the body, so a failure is reported as one</li>
              </ol>
            </>
          ) : (
            <>
              <p className="font-semibold text-sm mb-2">Twilio Setup</p>
              <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
                <li>Create account at twilio.com</li>
                <li>Get Account SID and Auth Token from Console</li>
                <li>Purchase a phone number for SMS sending</li>
                <li>Set up webhook URL for delivery status updates</li>
              </ol>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

// WhatsApp Configuration Form
const WhatsAppConfigForm: React.FC<{ channelId: string }> = ({ channelId }) => {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [channel, setChannel] = useState<NotificationChannel | null>(null);
  const [form, setForm] = useState({
    provider: 'twilio_whatsapp', account_sid: '', auth_token: '', from_number: 'whatsapp:+14155238886',
    business_name: '', webhook_url: '', is_active: false, test_phone: '',
  });

  const loadChannelData = async () => {
    try {
      setLoading(true);
      const channels = await notificationService.getWhatsAppChannels();
      if (channels.data && channels.data.length > 0) {
        const wa = channels.data[0];
        setChannel(wa);
        const c = wa.configuration;
        setForm({
          provider: c.provider || 'twilio_whatsapp', account_sid: c.account_sid || '',
          auth_token: c.auth_token || '', from_number: c.from_number || 'whatsapp:+14155238886',
          business_name: c.business_name || '', webhook_url: c.webhook_url || '',
          is_active: wa.is_active || false, test_phone: '',
        });
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { loadChannelData(); }, [channelId]);

  const updateForm = (key: string, value: any) => setForm((p) => ({ ...p, [key]: value }));

  const handleSave = async () => {
    setLoading(true);
    try {
      const result = await notificationService.saveWhatsAppChannel(form, channel?.id);
      if (result.error) toast({ title: `Failed: ${result.error}`, variant: 'destructive' });
      else { toast({ title: 'WhatsApp configuration saved', variant: 'success' }); await loadChannelData(); }
    } catch { toast({ title: 'Failed to save WhatsApp configuration', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const handleTest = async () => {
    if (!form.test_phone) { toast({ title: 'Please enter a test phone number', variant: 'destructive' }); return; }
    setTesting(true);
    try {
      let testId = channel?.id;
      if (!testId) {
        const temp = await notificationService.saveWhatsAppChannel({ ...form, is_active: false });
        if (temp.error) { toast({ title: `Failed: ${temp.error}`, variant: 'destructive' }); return; }
        testId = temp.data.id;
      }
      const result = await notificationService.testWhatsApp(testId, form.test_phone, form);
      if (result.error) toast({ title: `Test failed: ${result.error}`, variant: 'destructive' });
      else toast({ title: 'Test WhatsApp message sent', variant: 'success' });
    } catch { toast({ title: 'Failed to send test WhatsApp message', variant: 'destructive' }); }
    finally { setTesting(false); }
  };

  return (
    <Card>
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-2"><Phone className="h-4 w-4" /><span className="font-semibold">WhatsApp Configuration</span></div>
        <Badge>Twilio</Badge>
      </div>
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label>WhatsApp Provider *</Label>
            <Select value={form.provider} onValueChange={(v) => updateForm('provider', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="twilio_whatsapp">Twilio WhatsApp</SelectItem><SelectItem value="whatsapp_business">WhatsApp Business API</SelectItem><SelectItem value="360dialog">360Dialog</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>From Number *</Label><Input placeholder="whatsapp:+14155238886" value={form.from_number} onChange={(e) => updateForm('from_number', e.target.value)} /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Account SID *</Label><Input placeholder="AC..." value={form.account_sid} onChange={(e) => updateForm('account_sid', e.target.value)} /></div>
          <div className="space-y-2"><Label>Auth Token *</Label><Input type="password" value={form.auth_token} onChange={(e) => updateForm('auth_token', e.target.value)} /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Business Name *</Label><Input placeholder="LabBoxx LIMS" value={form.business_name} onChange={(e) => updateForm('business_name', e.target.value)} /></div>
          <div className="space-y-2"><Label>Webhook URL</Label><Input placeholder="https://your-domain.com/api/v1/notifications/webhooks/twilio/" value={form.webhook_url} onChange={(e) => updateForm('webhook_url', e.target.value)} /></div>
        </div>
        <Separator />
        <div className="grid grid-cols-3 gap-4 items-end">
          <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => updateForm('is_active', v)} /><Label>Active</Label></div>
          <div className="space-y-2"><Label>Test Phone</Label><Input placeholder="+1234567890" value={form.test_phone} onChange={(e) => updateForm('test_phone', e.target.value)} /></div>
          <Button variant="outline" onClick={handleTest} disabled={testing}><Send className="h-4 w-4 mr-1" />{testing ? 'Sending...' : 'Send Test Message'}</Button>
        </div>
        <Separator />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={loadChannelData}>Reset</Button>
          <Button onClick={handleSave} disabled={loading}><Save className="h-4 w-4 mr-1" />Save Configuration</Button>
        </div>
        <div className="bg-info-100 border border-info-200 rounded-md p-4 mt-4">
          <p className="font-semibold text-sm mb-2">WhatsApp Business Setup</p>
          <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
            <li>Apply for WhatsApp Business API access</li>
            <li>Complete Facebook Business verification</li>
            <li>Set up webhook endpoints for message delivery</li>
            <li>Test with sandbox number first (+14155238886)</li>
          </ol>
        </div>
      </div>
    </Card>
  );
};

// Main Channel Configuration Component
const ChannelConfiguration: React.FC = () => {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Notification Channel Configuration"
        icon={<Settings className="h-6 w-6" />}
        description="Configure Email, SMS, and WhatsApp notification channels."
      />
      <Tabs defaultValue="email">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="email"><Mail className="h-3 w-3 mr-1" />Email</TabsTrigger>
          <TabsTrigger value="sms"><MessageSquare className="h-3 w-3 mr-1" />SMS</TabsTrigger>
          <TabsTrigger value="whatsapp"><Phone className="h-3 w-3 mr-1" />WhatsApp</TabsTrigger>
        </TabsList>
        <TabsContent value="email" className="mt-4"><EmailConfigForm channelId="email" /></TabsContent>
        <TabsContent value="sms" className="mt-4"><SMSConfigForm channelId="sms" /></TabsContent>
        <TabsContent value="whatsapp" className="mt-4"><WhatsAppConfigForm channelId="whatsapp" /></TabsContent>
      </Tabs>
    </div>
  );
};

export default ChannelConfiguration;
